import { addCalendarDaysYmd } from './activityDateTime';
import {
  formatRecurrenceLabel,
  isoWeekdayFromYmd,
  monthlyChoicesFromStart,
  nextOccurrenceYmd,
  parseActivityRecurrence,
  parseYmdParts,
  recurrenceFormFromStored,
  recurrencePresetId,
  recurrencePresetOptions,
  type ActivityRecurrence,
  type RecurrencePresetId,
} from './activityRecurrence';

/**
 * Wiederkehrende To-dos: gleiche Regeln und Auswahl wie bei Aktivitäten, aber ohne Vorab-Anlage —
 * das nächste To-do entsteht erst beim Erledigen. Ende ist optional (Standard: ohne Ende).
 */
export type TaskRecurrence = Pick<ActivityRecurrence, 'freq' | 'interval' | 'weekdays' | 'monthly' | 'until'>;

const WORKWEEK_DAYS = [1, 2, 3, 4, 5];

/** `undefined` = nicht gesetzt, `null` = keine Wiederholung, `false` = ungültig. */
export function parseTaskRecurrence(value: unknown): TaskRecurrence | null | undefined | false {
  if (value === undefined) return undefined;
  if (value === null || value === '') return null;
  const parsed = parseActivityRecurrence(value);
  if ('error' in parsed) return false;
  const { freq, interval, weekdays, monthly, until } = parsed.value;
  return {
    freq,
    interval,
    ...(weekdays ? { weekdays } : {}),
    ...(monthly ? { monthly } : {}),
    ...(until ? { until } : {}),
  };
}

/** Regel zur Auswahl im Dropdown, verankert am Fälligkeitsdatum. */
export function taskRecurrenceFromPreset(
  preset: RecurrencePresetId,
  dueYmd: string,
  until?: string | null,
): TaskRecurrence | null {
  if (preset === 'none' || !parseYmdParts(dueYmd)) return null;
  const end = until ? { until } : {};
  if (preset === 'daily') return { freq: 'daily', interval: 1, ...end };
  if (preset === 'weekly') return { freq: 'weekly', interval: 1, weekdays: [isoWeekdayFromYmd(dueYmd)], ...end };
  if (preset === 'weekdays') return { freq: 'weekly', interval: 1, weekdays: [...WORKWEEK_DAYS], ...end };
  if (preset === 'yearly') return { freq: 'yearly', interval: 1, ...end };
  const choices = monthlyChoicesFromStart(dueYmd);
  const monthly =
    preset === 'monthly-bymonthday'
      ? choices.bymonthday
      : preset === 'monthly-lastweekday' && choices.lastWeekday
        ? choices.lastWeekday
        : choices.byweekday;
  return { freq: 'monthly', interval: 1, monthly, ...end };
}

export function taskRecurrencePreset(rec: TaskRecurrence | null | undefined, dueYmd: string): RecurrencePresetId {
  if (!rec) return 'none';
  return recurrencePresetId(recurrenceFormFromStored(rec, dueYmd), dueYmd);
}

export function taskRecurrenceOptions(rec: TaskRecurrence | null | undefined, dueYmd: string) {
  return recurrencePresetOptions(dueYmd, taskRecurrencePreset(rec, dueYmd));
}

/** Fälligkeit verschoben: gleiche Auswahl (z. B. „Wöchentlich“) am neuen Datum, Ende bleibt. */
export function rebaseTaskRecurrence(
  rec: TaskRecurrence | null | undefined,
  oldDueYmd: string | null,
  newDueYmd: string | null,
): TaskRecurrence | null {
  if (!rec || !newDueYmd) return null;
  if (!oldDueYmd) return rec;
  return taskRecurrenceFromPreset(taskRecurrencePreset(rec, oldDueYmd), newDueYmd, rec.until) ?? rec;
}

/**
 * Fälligkeit des nächsten To-dos: erster Termin nach der bisherigen Fälligkeit, aber nicht in der
 * Vergangenheit — wer drei Wochen nicht abhakt, bekommt ein To-do und nicht drei überfällige.
 */
export function nextTaskDueDate(rec: TaskRecurrence, dueYmd: string, todayYmd: string): string | null {
  const yesterday = addCalendarDaysYmd(todayYmd, -1);
  return nextOccurrenceYmd(rec, dueYmd, dueYmd > yesterday ? dueYmd : yesterday);
}

export function taskRecurrenceLabel(rec: TaskRecurrence, dueYmd: string | null): string {
  return formatRecurrenceLabel(rec, undefined, dueYmd ?? undefined);
}

/** Kurzformen für die KI-Tools — je eine Auswahl aus dem „Wiederholen“-Dropdown. */
export const TASK_REPEAT_OPTIONS = [
  'none',
  'daily',
  'weekly',
  'weekdays',
  'monthly',
  'monthly_day',
  'monthly_last',
  'yearly',
] as const;
export type TaskRepeat = (typeof TASK_REPEAT_OPTIONS)[number];

const REPEAT_PRESET: Record<TaskRepeat, RecurrencePresetId> = {
  none: 'none',
  daily: 'daily',
  weekly: 'weekly',
  weekdays: 'weekdays',
  monthly: 'monthly-byweekday',
  monthly_day: 'monthly-bymonthday',
  monthly_last: 'monthly-lastweekday',
  yearly: 'yearly',
};

/** Letzter gleicher Wochentag im Monat ab `ymd` (z. B. 13.10. Di → 27.10.). */
function lastSameWeekdayInMonth(ymd: string): string {
  let cur = ymd;
  for (;;) {
    const next = addCalendarDaysYmd(cur, 7);
    if (next.slice(0, 7) !== cur.slice(0, 7)) return cur;
    cur = next;
  }
}

/**
 * KI-Auswahl → Regel, verankert an der Fälligkeit. „monthly_last“ schiebt die Fälligkeit auf den
 * letzten gleichen Wochentag im Monat, damit Regel und Dropdown-Auswahl übereinstimmen.
 */
export function taskRecurrenceFromRepeat(
  repeat: TaskRepeat,
  dueYmd: string,
  until?: string | null,
): { recurrence: TaskRecurrence | null; dueDate: string } {
  const dueDate = repeat === 'monthly_last' ? lastSameWeekdayInMonth(dueYmd) : dueYmd;
  return { recurrence: taskRecurrenceFromPreset(REPEAT_PRESET[repeat], dueDate, until), dueDate };
}
